import {
    attachmentService,
    type AttachmentVO,
} from '@/services/auditService/attachment';

/** A4 width 210mm @ 96dpi */
export const A4_WIDTH_PX = 794;
/** Keep Tax Dialog as primary editable area on narrow viewports */
export const LAST_TAX_MIN_DIALOG_MAIN_PX = 560;

export const LAST_TAX_TARGET_PREFIX = 'last_tax';

export type LastTaxAttachmentMeta = {
    id: string;
    fileKey: string;
    fileName: string;
};

export function lastTaxYoaKey(yoa: string | null | undefined): string {
    return (yoa || '').trim() || '_';
}

/** Bind key for MinIO attachments: annualId (targetId) + YOA (in targetType). */
export function lastTaxTargetType(yoa: string | null | undefined): string {
    return `${LAST_TAX_TARGET_PREFIX}:${lastTaxYoaKey(yoa)}`;
}

export function calcLastTaxDrawerWidth(viewportWidth: number): number {
    const vw = Math.max(0, Number(viewportWidth) || 0);
    const maxDrawer = Math.max(240, vw - LAST_TAX_MIN_DIALOG_MAIN_PX);
    return Math.min(A4_WIDTH_PX, maxDrawer);
}

export function isPdfFile(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    return mime === 'application/pdf' || mime === 'application/x-pdf' || name.endsWith('.pdf');
}

function pickLatestAttachment(list: AttachmentVO[]): AttachmentVO | null {
    if (!list?.length) return null;
    const sorted = [...list].sort((a, b) =>
        String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')),
    );
    return sorted[0] || null;
}

export async function loadLatestLastTaxMeta(
    annualId: number,
    yoa: string | null | undefined,
): Promise<LastTaxAttachmentMeta | null> {
    if (!annualId) return null;
    const list = await attachmentService.listAttachments(lastTaxTargetType(yoa), annualId);
    const hit = pickLatestAttachment(list);
    if (!hit?.id || !hit.fileKey) return null;
    return {id: hit.id, fileKey: hit.fileKey, fileName: hit.fileName || 'last-tax.pdf'};
}

export async function uploadLastTaxPdf(params: {
    file: File;
    annualId: number;
    companyId: number;
    userId: number;
    yoa: string | null | undefined;
    replace?: boolean;
}): Promise<LastTaxAttachmentMeta> {
    const targetType = lastTaxTargetType(params.yoa);
    const targetId = params.annualId;
    if (!targetId) {
        throw new Error('Annual ID is required.');
    }
    if (!params.companyId) {
        throw new Error('Company ID is required.');
    }
    if (!params.userId) {
        throw new Error('User ID is required.');
    }

    if (params.replace) {
        const existing = await attachmentService.listAttachments(targetType, targetId);
        const ids = (existing || []).map((a) => a.id).filter(Boolean);
        if (ids.length > 0) {
            await attachmentService.batchDelete(ids);
        }
    }

    const result = await attachmentService.batchUploadAttachments([params.file], {
        targetType,
        targetId,
        userId: params.userId,
        companyId: params.companyId,
        annualId: params.annualId,
    });
    if (result.successCount < 1) {
        throw new Error(result.errors[0] || 'Upload failed.');
    }

    const meta = await loadLatestLastTaxMeta(params.annualId, params.yoa);
    if (!meta) {
        throw new Error('Upload succeeded but attachment was not found.');
    }
    return meta;
}

export async function createLastTaxPreviewUrl(params: {
    companyId: number;
    annualId: number;
    fileKey: string;
}): Promise<string> {
    const blob = await attachmentService.fetchAttachmentBlob(params);
    return URL.createObjectURL(new Blob([await blob.arrayBuffer()], {type: 'application/pdf'}));
}
